import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';

// Venezuela States → Cities mapping
const VENEZUELA_STATES_CITIES = {
  'Amazonas': ['Puerto Ayacucho'],
  'Anzoátegui': ['Barcelona', 'Puerto La Cruz', 'El Tigre', 'Anaco', 'Lechería'],
  'Apure': ['San Fernando de Apure', 'Guasdualito'],
  'Aragua': ['Maracay', 'La Victoria', 'Turmero', 'Cagua', 'Villa de Cura'],
  'Barinas': ['Barinas', 'Socopó', 'Ciudad Bolivia'],
  'Bolívar': ['Ciudad Guayana', 'Ciudad Bolívar', 'Upata', 'Caicara del Orinoco'],
  'Carabobo': ['Valencia', 'Puerto Cabello', 'Guacara', 'Los Guayos', 'Naguanagua'],
  'Cojedes': ['San Carlos', 'Tinaquillo'],
  'Delta Amacuro': ['Tucupita'],
  'Distrito Capital': ['Caracas'],
  'Falcón': ['Coro', 'Punto Fijo', 'Tucacas'],
  'Guárico': ['San Juan de los Morros', 'Calabozo', 'Valle de la Pascua', 'Zaraza'],
  'La Guaira': ['La Guaira', 'Catia La Mar', 'Maiquetía'],
  'Lara': ['Barquisimeto', 'Cabudare', 'Carora', 'Quíbor'],
  'Mérida': ['Mérida', 'El Vigía', 'Ejido', 'Tovar'],
  'Miranda': ['Los Teques', 'Guarenas', 'Guatire', 'Charallave', 'Ocumare del Tuy', 'Higuerote'],
  'Monagas': ['Maturín', 'Punta de Mata', 'Temblador'],
  'Nueva Esparta': ['La Asunción', 'Porlamar', 'Juan Griego', 'Pampatar'],
  'Portuguesa': ['Guanare', 'Acarigua', 'Araure'],
  'Sucre': ['Cumaná', 'Carúpano', 'Güiria'],
  'Táchira': ['San Cristóbal', 'Táriba', 'La Fría', 'San Antonio del Táchira'],
  'Trujillo': ['Valera', 'Trujillo', 'Boconó'],
  'Yaracuy': ['San Felipe', 'Yaritagua', 'Chivacoa'],
  'Zulia': ['Maracaibo', 'Cabimas', 'Ciudad Ojeda', 'Santa Bárbara del Zulia', 'Machiques', 'Los Puertos de Altagracia']
};

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+58');
  const [idDocument, setIdDocument] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [accountType, setAccountType] = useState('Persona');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [idDocError, setIdDocError] = useState('');
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Get cities based on selected state
  const availableCities = useMemo(() => {
    if (!state) return [];
    return VENEZUELA_STATES_CITIES[state] || [];
  }, [state]);

  // Validate cedula format: V-12345678 or E-12345678
  const validateIdDocument = (value) => {
    if (!value) {
      setIdDocError('');
      return true;
    }
    const cedulaRegex = /^[VvEe]-?\d{6,10}$/;
    if (!cedulaRegex.test(value.replace(/\s/g, ''))) {
      setIdDocError('Formato: V-12345678 o E-12345678');
      return false;
    }
    setIdDocError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        if (!fullName) {
          setError('Por favor ingresa tu nombre completo');
          setLoading(false);
          return;
        }
        if (idDocument && !validateIdDocument(idDocument)) {
          setError('Formato de cédula inválido. Usa V-12345678 o E-12345678');
          setLoading(false);
          return;
        }
        result = await register(email, password, fullName, {
          account_type: accountType,
          phone: phone,
          phone_country_code: phoneCountryCode,
          id_document: idDocument,
          address: address,
          state: state,
          city: city
        });
      }

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Ocurrió un error inesperado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_shipfwd/artifacts/qxbu8pxq_Gemini_Generated_Image_d4icd3d4icd3d4ic.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-indigo-900/40 to-purple-900/50"></div>
      
      <Card className="w-full max-w-2xl relative z-10 shadow-2xl" data-testid="login-card">
        <CardHeader className="space-y-4">
          <div className="flex justify-center mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_package-relay-2/artifacts/9nhv3a6e_traetelo%20d%20afuera%20logo%20%20%281%29.png" 
              alt="Trae Te Lo De Afuera Logo" 
              className="h-20 w-auto"
            />
          </div>
          
          <CardTitle className="text-2xl font-bold text-center">
            {isLogin ? 'CASILLERO EN ESTADOS UNIDOS' : 'Crea tu casillero'}
          </CardTitle>
          <CardDescription className="text-center">
            {isLogin 
              ? 'Ingresa tus credenciales para acceder a tu casillero'
              : 'Registrar un casillero es rápido y gratuito - sin compromisos ni contratos.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                {/* Tipo de cuenta */}
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setAccountType('Persona')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                      accountType === 'Persona'
                        ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Persona
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('Empresa')}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                      accountType === 'Empresa'
                        ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    Empresa
                  </button>
                </div>

                {/* Nombre y Apellido */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium">
                      Nombre <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="Tu nombre"
                      value={fullName.split(' ')[0] || ''}
                      onChange={(e) => {
                        const lastName = fullName.split(' ').slice(1).join(' ');
                        setFullName(e.target.value + (lastName ? ' ' + lastName : ''));
                      }}
                      required={!isLogin}
                      className="h-10"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium">
                      Apellido <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Tu apellido"
                      value={fullName.split(' ').slice(1).join(' ') || ''}
                      onChange={(e) => {
                        const firstName = fullName.split(' ')[0] || '';
                        setFullName(firstName + ' ' + e.target.value);
                      }}
                      className="h-10"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                {/* Móvil */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Móvil <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <select 
                      value={phoneCountryCode}
                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                      className="h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      data-testid="phone-country-code"
                    >
                      <option value="+58">+58 (VEN)</option>
                      <option value="+1">+1 (USA)</option>
                      <option value="+52">+52 (MÉX)</option>
                      <option value="+57">+57 (COL)</option>
                      <option value="+51">+51 (PER)</option>
                      <option value="+56">+56 (CHI)</option>
                      <option value="+54">+54 (ARG)</option>
                      <option value="+34">+34 (ESP)</option>
                      <option value="+55">+55 (BRA)</option>
                      <option value="+593">+593 (ECU)</option>
                      <option value="+507">+507 (PAN)</option>
                      <option value="+506">+506 (CRI)</option>
                    </select>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="424-1234567"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required={!isLogin}
                      className="flex-1 h-10"
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Ejemplo: +58 424-1234567
                  </p>
                </div>

                {/* Documento de Identidad y Dirección */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="idDocument" className="text-sm font-medium">
                      Cédula de Identidad
                    </Label>
                    <Input
                      id="idDocument"
                      type="text"
                      placeholder="V-12345678"
                      value={idDocument}
                      onChange={(e) => {
                        setIdDocument(e.target.value);
                        if (idDocError) validateIdDocument(e.target.value);
                      }}
                      onBlur={(e) => validateIdDocument(e.target.value)}
                      className={`h-10 ${idDocError ? 'border-red-400' : ''}`}
                      data-testid="id-document-input"
                    />
                    {idDocError && (
                      <p className="text-xs text-red-500">{idDocError}</p>
                    )}
                    <p className="text-xs text-gray-500">Tu cédula: V-12345678 o E-12345678</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium">
                      Dirección en Venezuela <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="address"
                      type="text"
                      placeholder="Av. Principal, Urbanización..."
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required={!isLogin}
                      className="h-10"
                      data-testid="address-input"
                    />
                  </div>
                </div>

                {/* Estado y Ciudad */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium">
                      Estado <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="state"
                      value={state}
                      onChange={(e) => {
                        setState(e.target.value);
                        setCity('');
                      }}
                      required={!isLogin}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      data-testid="state-select"
                    >
                      <option value="">Seleccione su estado</option>
                      {Object.keys(VENEZUELA_STATES_CITIES).map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium">
                      Ciudad <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required={!isLogin}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      data-testid="city-select"
                    >
                      <option value="">
                        {state ? 'Seleccione su ciudad' : 'Primero seleccione un estado'}
                      </option>
                      {availableCities.map(c => (
                        <option key={c} value={c.toLowerCase().replace(/\s+/g, '-')}>{c}</option>
                      ))}
                      {state && <option value="otra">Otra ciudad</option>}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Ciudad en Venezuela donde recibirá sus paquetes
                    </p>
                  </div>
                </div>
              </>
            )}
            
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email {!isLogin && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={isLogin ? 'tu@email.com' : 'tucorreo@email.com'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-10"
              />
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Contraseña {!isLogin && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-10"
              />
              {!isLogin && (
                <p className="text-xs text-gray-500">Mínimo 6 caracteres</p>
              )}
            </div>

            {!isLogin && (
              <div className="flex items-start space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="recaptcha"
                  required
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="recaptcha" className="text-sm text-gray-600">
                  No soy un robot - reCAPTCHA v3 & cambiar sus términos del servicio.{' '}
                  <a href="#" className="text-blue-600 hover:underline">
                    Tema medidas
                  </a>
                  {' - '}
                  <a href="#" className="text-blue-600 hover:underline">
                    Privacidad - Términos
                  </a>
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="error-message">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-11 text-base font-semibold bg-green-600 hover:bg-green-700" 
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              className="text-sm text-blue-600 hover:underline"
              data-testid="toggle-mode-button"
            >
              {isLogin 
                ? '¿No tienes cuenta? Regístrate aquí'
                : '¿Ya tienes una cuenta? Iniciar sesión'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
